import { MouseEventHandler, useMemo } from 'react';
import { SolanaAddress } from '@coinbase/cdp-core';
import { SendSolanaTransactionButton, SendSolanaTransactionButtonProps } from '@coinbase/cdp-react';
import {
  PublicKey,
  SystemProgram,
  SYSVAR_RECENT_BLOCKHASHES_PUBKEY,
  Transaction,
} from '@solana/web3.js';

type Props = {
  sourceAddress?: SolanaAddress;
  destinationAddress?: SolanaAddress;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onError?: SendSolanaTransactionButtonProps['onError'];
  onSuccess?: SendSolanaTransactionButtonProps['onSuccess'];
};

function createAndEncodeTransaction(
  sourceAddress: SolanaAddress,
  destinationAddress: SolanaAddress,
) {
  const recipientAddress = new PublicKey(destinationAddress);

  const fromPubkey = new PublicKey(sourceAddress);

  const transferAmount = 1; // 1 Lamport

  const transaction = new Transaction().add(
    SystemProgram.transfer({
      fromPubkey,
      toPubkey: recipientAddress,
      lamports: transferAmount,
    }),
  );

  transaction.recentBlockhash = SYSVAR_RECENT_BLOCKHASHES_PUBKEY.toBase58();
  transaction.feePayer = fromPubkey;

  const serializedTransaction = transaction.serialize({
    requireAllSignatures: false,
  });

  return Buffer.from(serializedTransaction).toString('base64');
}

function SendSolanaTransaction({
  sourceAddress,
  destinationAddress,
  onClick,
  onError,
  onSuccess,
}: Props) {
  const transaction = useMemo<SendSolanaTransactionButtonProps['transaction'] | undefined>(() => {
    if (!destinationAddress || !sourceAddress) {
      return undefined;
    }
    return createAndEncodeTransaction(sourceAddress, destinationAddress);
  }, [destinationAddress, sourceAddress]);

  if (!destinationAddress || !sourceAddress || !transaction) {
    return null;
  }

  return (
    <SendSolanaTransactionButton
      account={sourceAddress}
      network="solana-devnet"
      transaction={transaction}
      onClick={onClick}
      onError={onError}
      onSuccess={onSuccess}
    >
      Send transaction
    </SendSolanaTransactionButton>
  );
}

export { SendSolanaTransaction };
